import React, { useState } from 'react';

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Flex,
  HoverCard,
  List,
  Menu,
  Modal,
  Paper,
  PaperProps,
  Space,
  Text
} from '@mantine/core';
import {
  IconAlertCircle,
  IconBrandMeta,
  IconCircleNumber1Filled,
  IconCircleNumber2Filled,
  IconCircleNumber3Filled,
  IconDotsVertical,
  IconExternalLink,
  IconPhone,
  IconTrash,
  IconUserScan
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { SmartAssistantConnectedBadge } from 'components/SmartAssistantConnectedBadge';
import { useTranslation } from 'react-i18next';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_BY_WHATSAPP_PHONE_NUMBER_ID_QUERY_KEY } from 'shared/constants/query-keys';
import { SmartAssistantType } from 'shared/interfaces/SmartAssistant';
import { WhatsAppAccount, WhatsAppPhoneNumber, WhatsAppPhoneNumberStatus } from 'shared/interfaces/WhatsAppAccount';

const DISPLAY_NAME_VALIDATION_REQUIRED_REFERENCE = 'https://www.facebook.com/business/help/757569725593362';

interface WhatsAppPhoneNumberTileProps extends PaperProps {
  account: WhatsAppAccount;
  phoneNumber: WhatsAppPhoneNumber;
}
export function WhatsAppPhoneNumberTile({ account, phoneNumber, ...paperProps }: WhatsAppPhoneNumberTileProps) {
  const { t } = useTranslation();

  const [deletePhoneModalOpened, setDeletePhoneModalOpened] = useState(false);

  const managePhoneNumberMetaUrl = `https://business.facebook.com/wa/manage/phone-numbers/?waba_id=${account.whatsAppId}`;
  const editPhoneNumberProfileOnMetaLink = `https://business.facebook.com/wa/manage/phone-numbers/?waba_id=${account.whatsAppId}&childRoute=PHONE_PROFILE%2FPROFILE&phone_number=${phoneNumber.standardizedPhoneNumber}`;

  const connectedSmartAssistantQuery = useQuery({
    queryKey: [SMART_ASSISTANT_BY_WHATSAPP_PHONE_NUMBER_ID_QUERY_KEY, phoneNumber.whatsAppId],
    queryFn: () => SmartAssistantService.getSmartAssistantByWhatsAppPhoneNumberId(phoneNumber.whatsAppId)
  });

  const StatusBadge = ({ status }: { status?: WhatsAppPhoneNumberStatus }) => {
    let target = null;
    let dropdown = null;

    if (status === WhatsAppPhoneNumberStatus.INVALID) {
      target = (
        <Badge color="red" variant="light" style={{ cursor: 'default' }} mx="md">
          {t(`WHATSAPP.PHONE_NUMBER_STATUS.INVALID.TITLE`)}
        </Badge>
      );
      dropdown = (
        <Text>
          {t(`WHATSAPP.PHONE_NUMBER_STATUS.INVALID.DESCRIPTION`)}
          {t(`WHATSAPP.PHONE_NUMBER_STATUS.INVALID.REFERENCE.PART_1`)}
          <Text
            component="a"
            href={managePhoneNumberMetaUrl}
            target="_blank"
            style={{ cursor: 'pointer', textDecoration: 'underline' }}>
            {t(`WHATSAPP.PHONE_NUMBER_STATUS.INVALID.REFERENCE.PART_2`)}
          </Text>
        </Text>
      );
    }
    if (status === WhatsAppPhoneNumberStatus.DISPLAY_NAME_VALIDATION_REQUIRED) {
      target = (
        <Badge color="orange" variant="light" style={{ cursor: 'default' }} mx="md">
          {t(`WHATSAPP.PHONE_NUMBER_STATUS.DISPLAY_NAME_VALIDATION_REQUIRED.TITLE`)}
        </Badge>
      );
      dropdown = (
        <Text>
          {t(`WHATSAPP.PHONE_NUMBER_STATUS.DISPLAY_NAME_VALIDATION_REQUIRED.DESCRIPTION`)}
          {t(`WHATSAPP.PHONE_NUMBER_STATUS.DISPLAY_NAME_VALIDATION_REQUIRED.REFERENCE.PART_1`)}
          <Text
            component="a"
            href={DISPLAY_NAME_VALIDATION_REQUIRED_REFERENCE}
            target="_blank"
            style={{ cursor: 'pointer', textDecoration: 'underline' }}>
            {t(`WHATSAPP.PHONE_NUMBER_STATUS.DISPLAY_NAME_VALIDATION_REQUIRED.REFERENCE.PART_2`)}
          </Text>
        </Text>
      );
    }

    if (status === WhatsAppPhoneNumberStatus.REGISTRATION_REQUIRED) {
      target = (
        <Badge color="red" variant="light" style={{ cursor: 'default' }} mx="md">
          {t(`WHATSAPP.PHONE_NUMBER_STATUS.REGISTRATION_REQUIRED.TITLE`)}
        </Badge>
      );
      dropdown = <Text>{t(`WHATSAPP.PHONE_NUMBER_STATUS.REGISTRATION_REQUIRED.DESCRIPTION`)}</Text>;
    }

    return target && dropdown ? (
      <HoverCard styles={{ dropdown: { maxWidth: 250 } }}>
        <HoverCard.Target>{target}</HoverCard.Target>
        <HoverCard.Dropdown>{dropdown}</HoverCard.Dropdown>
      </HoverCard>
    ) : null;
  };

  return (
    <>
      <Paper p={5} px={10} withBorder {...paperProps}>
        <Flex direction="row" justify="space-between" align="center" w="100%" h="100%">
          <Flex direction="row" align="center">
            <IconPhone size={22} />
            <Space w={5} />
            <Text>{phoneNumber.displayedNumber}</Text>
            <StatusBadge status={phoneNumber.status} />
          </Flex>

          <Flex direction="row" align="center">
            {connectedSmartAssistantQuery.isFetched && !connectedSmartAssistantQuery.isError && (
              <>
                <SmartAssistantConnectedBadge
                  smartAssistant={connectedSmartAssistantQuery.data}
                  type={SmartAssistantType.WHATSAPP}
                />
                <Space w={15} />
              </>
            )}
            <Menu position="right-start" withArrow arrowPosition="center" offset={0}>
              <Menu.Target>
                <ActionIcon onClick={(event) => event.stopPropagation()}>
                  <IconDotsVertical />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item icon={<IconBrandMeta />} onClick={() => window.open(managePhoneNumberMetaUrl, '_blank')}>
                  {t('WHATSAPP.MANAGE_PHONE_NUMBER')}
                </Menu.Item>
                <Menu.Item
                  icon={<IconUserScan />}
                  onClick={() => window.open(editPhoneNumberProfileOnMetaLink, '_blank')}>
                  {t('WHATSAPP.EDIT_PHONE_NUMBER_PROFILE')}
                </Menu.Item>
                <Menu.Item icon={<IconTrash />} color="red" onClick={() => setDeletePhoneModalOpened(true)}>
                  {t('APP.ACTIONS.DELETE')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </Flex>
      </Paper>
      <Modal
        opened={deletePhoneModalOpened}
        onClose={() => setDeletePhoneModalOpened(false)}
        title={t('WHATSAPP.DELETE.PHONE_NUMBER.TITLE')}
        size="lg"
        centered>
        {connectedSmartAssistantQuery.data && (
          <Alert mb="xs" icon={<IconAlertCircle />} title={t('WHATSAPP.DELETE.PHONE_NUMBER.WARNING.TITLE')} color="red">
            <Text fz="md">{t('WHATSAPP.DELETE.PHONE_NUMBER.WARNING.DESCRIPTION')}</Text>
          </Alert>
        )}
        <Paper bg="gray.1" p="md">
          <Text fz="md">{t('WHATSAPP.DELETE.PHONE_NUMBER.INSTRUCTIONS')}</Text>
          <List size="md" mt="xs" ml="md" type="ordered">
            <List.Item icon={<IconCircleNumber1Filled />}>{t('WHATSAPP.DELETE.PHONE_NUMBER.STEP_1')}</List.Item>
            <List.Item icon={<IconCircleNumber2Filled />}>{t('WHATSAPP.DELETE.PHONE_NUMBER.STEP_2')}</List.Item>
            <List.Item icon={<IconCircleNumber3Filled />}>{t('WHATSAPP.DELETE.PHONE_NUMBER.STEP_3')}</List.Item>
          </List>
        </Paper>
        <Flex justify="flex-end">
          <Button
            mt="md"
            variant="outline"
            component="a"
            href={managePhoneNumberMetaUrl}
            target="_blank"
            rel="noopener noreferrer"
            rightIcon={<IconExternalLink />}>
            {t('WHATSAPP.DELETE.PHONE_NUMBER.CTA')}
          </Button>
        </Flex>
      </Modal>
    </>
  );
}
